/**
 * Inspector — right-rail detail panel.
 *
 *   <Inspector>
 *     <Inspector.Head eyebrow={<>Záznam <b>№ 03</b></>} title="…" sub="…" />
 *     <Inspector.Body>
 *       <Inspector.Section heading="Specifikace">
 *         <FieldRow k="Sezóna" v="Letní" />
 *         <FieldRow k="DOT" v="3722" mono />
 *       </Inspector.Section>
 *     </Inspector.Body>
 *     <Inspector.Foot>{actions}</Inspector.Foot>
 *   </Inspector>
 *
 * Call sites keep listing the slots in reading order (Head · Body · Foot), but
 * the shell hoists the action bar (`Inspector.Foot`) directly under the Head so
 * the primary CTAs sit at the top of the rail — within reach without scrolling
 * past the field list — mirroring the top-aligned actions on the detail pages.
 *
 * Foot button rule: exactly ONE blue `primary` Button — the pane's main action
 * — in the last slot. Primary is the terminal workflow action where one
 * exists (Splnit, Vyřídit), otherwise Detail. When a workflow verb owns the
 * blue slot, Detail renders as `ink` (second-tier CTA) immediately before it
 * — so if the bar wraps, Detail still closes the first row and the primary
 * gets its own row. All other buttons are `secondary`. When permissions hide
 * the workflow verb, Detail flips to `primary` so one blue button always
 * remains. `ink` has no other use in a Foot — elsewhere it belongs to form
 * savebars.
 *
 * Source: .agents/design/project/artboard-radical.jsx:242-313
 */

import { Children, isValidElement, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

interface InspectorProps {
  children: ReactNode;
  className?: string;
  /**
   * When provided, the inspector body crossfades (180 ms opacity tween) every
   * time the key changes. Pass the currently selected record's id so swapping
   * rows in the paired list dissolves the right-rail content instead of
   * snapping. `null` / `undefined` is treated as the "empty" state.
   *
   * Backward compatible: omit the prop and the inspector renders without any
   * AnimatePresence wrapper — same DOM as pre-12.2.
   */
  selectionKey?: string | number | null;
}

/**
 * Reorder the slot children so the action bar renders right under the head:
 * Head · Foot (actions) · everything else (Body, empty-state nodes, …) — while
 * preserving each group's original relative order. Non-slot children (e.g. the
 * centered empty-state div) fall through into the trailing group untouched.
 */
function hoistFoot(children: ReactNode): ReactNode {
  const arr = Children.toArray(children);
  const head: ReactNode[] = [];
  const foot: ReactNode[] = [];
  const rest: ReactNode[] = [];
  for (const child of arr) {
    if (isValidElement(child) && child.type === Head) head.push(child);
    else if (isValidElement(child) && child.type === Foot) foot.push(child);
    else rest.push(child);
  }
  return [...head, ...foot, ...rest];
}

export function Inspector({ children, className = '', selectionKey }: InspectorProps) {
  const reduce = useReducedMotion();
  const ordered = hoistFoot(children);
  const shell = (
    <aside
      className={
        'flex flex-col overflow-hidden border-l border-line bg-paper ' +
        className
      }
    >
      {selectionKey === undefined ? (
        ordered
      ) : (
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={selectionKey ?? '__empty__'}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? { opacity: 1 } : { opacity: 0 }}
            transition={{
              duration: reduce ? 0 : 0.18,
              ease: [0.2, 0, 0.13, 1],
            }}
            className="flex flex-1 flex-col overflow-hidden"
          >
            {ordered}
          </motion.div>
        </AnimatePresence>
      )}
    </aside>
  );
  return shell;
}

interface InspectorHeadProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  /** Optional right-side eyebrow content (e.g. status). */
  eyebrowEnd?: ReactNode;
  className?: string;
}

function Head({ eyebrow, eyebrowEnd, title, sub, className = '' }: InspectorHeadProps) {
  return (
    <header className={'border-b border-line px-6 pb-4 pt-[22px] ' + className}>
      {(eyebrow || eyebrowEnd) && (
        <div className="mb-2 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
          {eyebrow && <span className="[&_b]:font-bold [&_b]:text-accent-text">{eyebrow}</span>}
          {eyebrowEnd && <span className="ml-auto">{eyebrowEnd}</span>}
        </div>
      )}
      <h2 className="text-[22px] font-bold leading-[1.15] tracking-[-0.02em] text-ink">
        {title}
      </h2>
      {sub && <p className="mt-1 font-mono text-[12.5px] text-ink-3">{sub}</p>}
    </header>
  );
}
Inspector.Head = Head;

interface InspectorBodyProps {
  children: ReactNode;
  className?: string;
}

function Body({ children, className = '' }: InspectorBodyProps) {
  return <div className={'flex-1 overflow-auto px-6 py-4 ' + className}>{children}</div>;
}
Inspector.Body = Body;

interface InspectorSectionProps {
  /** Mono uppercase eyebrow above the section. */
  heading?: ReactNode;
  children: ReactNode;
  className?: string;
}

function Section({ heading, children, className = '' }: InspectorSectionProps) {
  return (
    <section
      className={'border-b border-line py-3 last:border-b-0 ' + className}
    >
      {heading && (
        <div className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3">
          {heading}
        </div>
      )}
      {children}
    </section>
  );
}
Inspector.Section = Section;

interface InspectorFootProps {
  children: ReactNode;
  className?: string;
}

function Foot({ children, className = '' }: InspectorFootProps) {
  return (
    <footer
      className={
        // flex-auto (not flex-1): basis stays at content width so a crowded
        // bar wraps to a second row instead of overflowing past the right
        // padding — flex-1's zero basis can never trigger a wrap, it only
        // overflows once the labels' min-content widths exceed the rail.
        'flex flex-wrap gap-2 border-b border-line px-6 py-3.5 [&>*]:flex-auto ' +
        className
      }
    >
      {children}
    </footer>
  );
}
Inspector.Foot = Foot;

export interface FieldRowProps {
  k: ReactNode;
  v: ReactNode;
  /** Render the value in mono — used for VINs, codes, dates. */
  mono?: boolean;
  className?: string;
}

export function FieldRow({ k, v, mono = false, className = '' }: FieldRowProps) {
  return (
    <div
      className={'flex justify-between gap-3 py-1.5 text-[12.5px] ' + className}
    >
      <dt className="text-ink-3">{k}</dt>
      <dd
        className={
          'text-right font-medium text-ink ' +
          (mono ? 'font-mono text-[11.5px] font-normal' : '')
        }
      >
        {v}
      </dd>
    </div>
  );
}
